import React, {
  createContext,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react"

import { Button } from "@coval/ui"
import {
  ArrowUpRight,
  ChevronsDownUp,
  ChevronsUpDown,
  RotateCw,
} from "lucide-react"

import { useToggleWithoutSelection } from "@/hooks/useToggleWithoutSelection"

/**
 * Compact metric card for displaying a single metric result.
 * Used across simulation deep-dive, conversation results, and human review.
 *
 * Supports horizontal strip layout (isHorizontal) and vertical grid layout.
 *
 * Auto-detects whether valueNode content is chip-like (contains a flex-wrap
 * container) or text-like, and applies the appropriate overflow strategy:
 * - Chips: measures actual row positions and clips to exactly 2 full rows
 * - Text: applies line-clamp-2 via class
 *
 * Runs before paint (useLayoutEffect), so no visual flash occurs.
 */
const MetricCardExpansionContext = createContext<{
  setExpanded: (v: boolean) => void
}>({ setExpanded: () => {} })

// Cards shrink with their column rather than holding a floor that makes the
// list scroll sideways — the card itself reflows once it runs out of room.
export const METRIC_CARD_LIST_CLASS =
  "flex flex-col gap-2 min-w-0 max-w-[800px] mx-auto"

function applyValueClamp(container: HTMLDivElement): void {
  // Reset any previous state so measurement reflects natural layout
  container.style.maxHeight = "none"
  container.style.overflow = "visible"
  container.classList.remove("line-clamp-2")

  // Force synchronous reflow so getBoundingClientRect is accurate
  void container.offsetHeight

  const flexWrap = container.querySelector(".flex-wrap")

  if (flexWrap && flexWrap.children.length > 0) {
    // Chip content: measure actual row positions and clip to 2 full rows
    const containerRect = container.getBoundingClientRect()
    const items = Array.from(flexWrap.children) as HTMLElement[]

    let rowCount = 0
    let currentRowBottom = -Infinity
    let clampHeight = 0

    for (const item of items) {
      const rect = item.getBoundingClientRect()
      const relTop = rect.top - containerRect.top
      const relBottom = rect.bottom - containerRect.top

      if (relTop >= currentRowBottom - 2) {
        rowCount++
        if (rowCount > 2) {
          break
        }
        currentRowBottom = relBottom
      } else {
        currentRowBottom = Math.max(currentRowBottom, relBottom)
      }

      clampHeight = Math.max(clampHeight, relBottom)
    }

    container.style.maxHeight = `${clampHeight}px`
    container.style.overflow = "hidden"
  } else {
    // Text content: use CSS line-clamp
    container.style.maxHeight = ""
    container.style.overflow = ""
    container.classList.add("line-clamp-2")
  }
}

interface MetricResultCardProps {
  title: string
  description: string
  valueNode: React.ReactNode
  headerValue?: string
  headerValueNode?: React.ReactNode
  topRightStatusNode?: React.ReactNode
  subtitleNode?: React.ReactNode
  humanReviewNode?: React.ReactNode
  onExpand?: () => void
  isActive?: boolean
  onClick?: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  onRerun?: () => void
  showRerunButton?: boolean
  isRerunning?: boolean
  /** When set, disables the rerun button and replaces its tooltip with this reason. */
  rerunDisabledReason?: string
  /** When provided, shows a hover action that opens this result in the metric editor. */
  onJumpToMetric?: () => void
  /** When true, renders as a fixed-size horizontal strip card (w-64 h-[70px]) */
  isHorizontal?: boolean
  /** When true, removes individual card border and rounded corners (for use in grid layouts) */
  borderless?: boolean
  /** Rendered across the card's full width, below the AI result / human
   *  review row. For review UI too wide for the half-width column. */
  humanReviewDetailNode?: React.ReactNode
  tabIndex?: number
}

export const MetricResultCard: React.FC<MetricResultCardProps> = ({
  title,
  description,
  valueNode,
  headerValue,
  headerValueNode,
  topRightStatusNode,
  subtitleNode,
  humanReviewNode,
  onExpand,
  isActive = false,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onRerun,
  showRerunButton = false,
  isRerunning = false,
  rerunDisabledReason,
  onJumpToMetric,
  isHorizontal = false,
  borderless = false,
  humanReviewDetailNode,
  tabIndex = 0,
}) => {
  const valueRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!valueRef.current) {
      return
    }
    applyValueClamp(valueRef.current)
  }, [])

  const RERUN_LABEL = "Rerun metric"
  const RERUN_LABEL_RUNNING = "Rerunning metric..."

  const expansionContextValue = useMemo(
    () => ({
      setExpanded: (v: boolean) => {
        if (v) {
          onExpand?.()
        }
      },
    }),
    [onExpand],
  )

  const stopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  // Toggle on a genuine click, but not while the user is selecting text.
  const toggleHandlers = useToggleWithoutSelection(onClick)

  const handleRerunClick = useCallback(
    (e: React.MouseEvent) => {
      stopPropagation(e)
      onRerun?.()
    },
    [stopPropagation, onRerun],
  )

  const JUMP_LABEL = "Open in metric editor"

  const handleJumpClick = useCallback(
    (e: React.MouseEvent) => {
      stopPropagation(e)
      onJumpToMetric?.()
    },
    [stopPropagation, onJumpToMetric],
  )

  const jumpButton = onJumpToMetric ? (
    <Button
      variant="ghost"
      icon
      onClick={handleJumpClick}
      className="-my-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
      tooltip={JUMP_LABEL}
    >
      <ArrowUpRight />
    </Button>
  ) : null

  const titleClasses = `font-semibold text-coval-text ${
    isHorizontal ? "text-sm truncate" : "text-base line-clamp-2 break-words"
  }`

  return (
    <div
      // The card tracks its own width, not the viewport's: the surfaces it sits
      // on open and resize side panels beside it, so the column can be narrow on
      // a wide screen.
      className={`@container/card ${isHorizontal ? "p-3" : "pt-4 pb-4 px-4"} ${
        onClick != null ? "cursor-pointer" : ""
      } transition-colors duration-200 relative group/card flex flex-col bg-surface-default focus-visible:outline-none ${
        borderless ? "" : "rounded"
      } ${isHorizontal ? "w-64 h-[70px]" : ""}`}
      title={title}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // The whole card toggles, but text stays selectable: the click handler
      // ignores drag-selections and live selections, and the value/reasoning
      // region below stops propagation so it never toggles.
      {...(onClick != null ? toggleHandlers : {})}
      role={onClick != null ? "button" : undefined}
      tabIndex={onClick != null ? tabIndex : undefined}
      aria-label={onClick != null ? `${title}: ${description}` : undefined}
      onKeyDown={
        onClick != null
          ? (e) => {
              // Only the card's own keystrokes toggle it. Interactive children
              // — vote buttons, note inputs — bubble Enter/Space up here, and
              // activating one must not also select the card.
              if (e.target !== e.currentTarget) {
                return
              }
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
    >
      {topRightStatusNode != null && (
        <div
          aria-live="polite"
          className={`pointer-events-none absolute right-4 top-3 z-10 text-right transition-opacity duration-200 ${
            onExpand != null
              ? "group-hover/card:opacity-0 group-focus-visible/card:opacity-0"
              : ""
          } ${onExpand != null && isActive ? "opacity-0" : ""}`}
        >
          {topRightStatusNode}
        </div>
      )}
      {onExpand != null && (
        // Explicit collapse/expand control. The card body no longer toggles on
        // click (so text stays selectable), so this icon button is the way to
        // collapse an expanded card — kept visible while active, revealed on
        // card hover while collapsed. Ghost variant, so the background only
        // shows on hover.
        <Button
          variant="ghost"
          icon
          tooltip={isActive ? "Collapse" : "Expand"}
          data-testid={
            isActive
              ? "metric-card-collapse-button"
              : "metric-card-expand-button"
          }
          onClick={(e) => {
            e.stopPropagation()
            onExpand()
          }}
          className={`absolute right-3 top-2 z-20 transition-opacity duration-200 ${
            isActive
              ? "opacity-100"
              : "opacity-0 group-hover/card:opacity-100 group-focus-visible/card:opacity-100"
          }`}
        >
          {isActive ? <ChevronsDownUp /> : <ChevronsUpDown />}
        </Button>
      )}
      {/* Side by side while the card has room; below that the human review
          stacks under the AI result rather than being squeezed into a half. */}
      <div className="flex flex-col gap-4 @md/card:flex-row">
        <div
          className={`min-w-0 ${humanReviewNode != null ? "w-full @md/card:w-1/2" : "flex-1"}`}
        >
          {valueNode != null ? (
            <>
              <div className="min-w-0">
                <div className="flex items-center gap-1">
                  <div className={titleClasses} title={title}>
                    {title}
                  </div>
                  {showRerunButton && onRerun && (
                    <Button
                      variant="ghost"
                      icon
                      onClick={handleRerunClick}
                      disabled={isRerunning || Boolean(rerunDisabledReason)}
                      className="-my-1 shrink-0 opacity-0 group-hover/card:opacity-100 transition-opacity duration-200"
                      tooltip={isRerunning ? RERUN_LABEL_RUNNING : RERUN_LABEL}
                      disabledReason={rerunDisabledReason}
                      aria-label={RERUN_LABEL}
                    >
                      <RotateCw />
                    </Button>
                  )}
                  {jumpButton}
                </div>
                {subtitleNode}
                {(headerValue != null || headerValueNode != null) && (
                  <div className="flex items-center gap-2 text-base font-semibold text-foreground mt-1">
                    {headerValueNode != null
                      ? headerValueNode
                      : headerValue != null && (
                          <span className="whitespace-nowrap">
                            {headerValue}
                          </span>
                        )}
                  </div>
                )}
              </div>
              <div
                ref={valueRef}
                className="min-w-0 mt-2 flex items-start"
                title={description}
                // Reasoning/value prose is the primary selection target: keep it
                // fully selectable and never toggle from clicks that land here.
                onClick={stopPropagation}
              >
                {valueNode}
              </div>
            </>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-1 min-w-0">
                  <div className={titleClasses} title={title}>
                    {title}
                  </div>
                  {showRerunButton && onRerun && (
                    <Button
                      variant="ghost"
                      icon
                      onClick={(e) => {
                        stopPropagation(e)
                        onRerun()
                      }}
                      disabled={isRerunning || Boolean(rerunDisabledReason)}
                      className="-my-1 shrink-0 opacity-0 group-hover/card:opacity-100 transition-opacity duration-200"
                      tooltip={
                        isRerunning ? "Rerunning metric..." : "Rerun metric"
                      }
                      disabledReason={rerunDisabledReason}
                      aria-label="Rerun metric"
                    >
                      <RotateCw />
                    </Button>
                  )}
                  {jumpButton}
                </div>
                {headerValue != null && (
                  <div className="text-base font-semibold text-foreground whitespace-nowrap">
                    {headerValue}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {humanReviewNode != null && (
          <MetricCardExpansionContext.Provider value={expansionContextValue}>
            <div
              role="presentation"
              data-human-review
              className="w-full @md/card:w-1/2 flex flex-col items-start"
              onClick={(e) => {
                if (e.target !== e.currentTarget) {
                  stopPropagation(e)
                }
              }}
            >
              {humanReviewNode}
            </div>
          </MetricCardExpansionContext.Provider>
        )}
      </div>
      {humanReviewDetailNode != null && (
        <MetricCardExpansionContext.Provider value={expansionContextValue}>
          <div
            role="presentation"
            data-human-review-detail
            className="mt-4 w-full min-w-0"
            onClick={(e) => {
              if (e.target !== e.currentTarget) {
                stopPropagation(e)
              }
            }}
          >
            {humanReviewDetailNode}
          </div>
        </MetricCardExpansionContext.Provider>
      )}
    </div>
  )
}
